import { API_URL } from 'config';
import { useAuth } from 'context/AuthContext';
import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  useCallback,
} from 'react';
import { NotificationsRealtimeClient } from 'services/notifications/notificationsRealtime';
import {
  getNotificationsPaged,
  putNotificationMarkAsRead,
  putNotificationDelete,
} from 'services/notificationsServices';

const NotificationsRealtimeContext = createContext(null);

export function NotificationsRealtimeProvider({ children }) {
  const { token, isAuthenticated } = useAuth();

  const [notifications, setNotifications] = useState([]);
  const [isConnected, setIsConnected] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [unreadTotal, setUnreadTotal] = useState(0);

  const apiUrl = API_URL;

  const hubUrl = `${apiUrl}/hubs/notifications`;

  const client = useMemo(() => {
    return new NotificationsRealtimeClient(hubUrl, () => token);
  }, [hubUrl, token]);

  const clearLocal = useCallback(() => {
    setNotifications([]);
    setUnreadTotal(0);
  }, []);

  const loadNotifications = useCallback(
    async (nextPage = 1) => {
      if (loading) return;

      setLoading(true);

      try {
        const data = await getNotificationsPaged.getPaged({
          page: nextPage,
          pageSize: 20,
        });

        setNotifications((prev) => {
          const ids = new Set(prev.map((n) => n.id));

          const normalized = data.items.map((n) => ({
            ...n,
            isRead: Boolean(n.isRead),
          }));

          return [...prev, ...normalized.filter((n) => !ids.has(n.id))];
        });

        setHasMore(data.items.length === 20);
        setPage(nextPage);
      } finally {
        setLoading(false);
      }
    },
    [loading]
  );

  const onReceived = useCallback((notification) => {
    setNotifications((prev) => {
      const exists = prev.some((n) => n.id === notification.id);
      if (exists) return prev;

      return [
        {
          ...notification,
          isRead: false,
        },
        ...prev,
      ];
    });

    setUnreadTotal((prev) => prev + 1);
  }, []);

  const markAsRead = useCallback(async (notificationId) => {
    let wasUnread = false;

    setNotifications((prev) =>
      prev.map((n) => {
        if (n.id === notificationId && !n.isRead) {
          wasUnread = true;
          return { ...n, isRead: true };
        }
        return n;
      })
    );

    if (wasUnread) {
      setUnreadTotal((prev) => Math.max(prev - 1, 0));
    }

    try {
      await putNotificationMarkAsRead.putMarkAsRead(notificationId);
    } catch (err) {
      console.error(err);
    }
  }, []);

  const refreshUnreadCount = useCallback(async () => {
    try {
      const data = await getNotificationsPaged.getPaged({
        onlyUnread: true,
        page: 1,
        pageSize: 1,
      });

      if (typeof data.total === 'number') {
        setUnreadTotal(data.total);
      }
    } catch (err) {
      console.error('Failed to refresh unread count', err);
    }
  }, []);

  const deleteNotification = useCallback(async (notificationId) => {
    let wasUnread = false;

    setNotifications((prev) => {
      const target = prev.find((n) => n.id === notificationId);
      if (target && !target.isRead) {
        wasUnread = true;
      }
      return prev.filter((n) => n.id !== notificationId);
    });

    if (wasUnread) {
      setUnreadTotal((prev) => Math.max(prev - 1, 0));
    }

    try {
      await putNotificationDelete.putDelete(notificationId);
    } catch (err) {
      console.error(err);
    }
  }, []);

  useEffect(() => {
    if (!isAuthenticated || !token) {
      client.disconnect();
      setIsConnected(false);
      clearLocal();
      return;
    }

    loadNotifications(1);

    let mounted = true;

    client.connect({
      onReceived,
      onConnected: () => {
        mounted && setIsConnected(true);
      },
      onDisconnected: () => {
        mounted && setIsConnected(false);
      },
      onError: (err) => {
        mounted && setIsConnected(false);
      },
    });

    return () => {
      mounted = false;
      client.disconnect();
    };
  }, [isAuthenticated, token, client, onReceived, clearLocal]);

  useEffect(() => {
    if (isAuthenticated && token) {
      refreshUnreadCount();
    }
  }, [isAuthenticated, token, refreshUnreadCount]);

  return (
    <NotificationsRealtimeContext.Provider
      value={{
        notifications,
        unreadCount: unreadTotal,
        isConnected,
        loadMore: () => loadNotifications(page + 1),
        hasMore,
        loading,
        markAsRead,
        deleteNotification,
        refreshUnreadCount,
      }}
    >
      {children}
    </NotificationsRealtimeContext.Provider>
  );
}

export function useNotificationsRealtime() {
  const ctx = useContext(NotificationsRealtimeContext);
  if (!ctx) {
    throw new Error(
      'useNotificationsRealtime must be used within NotificationsRealtimeProvider'
    );
  }
  return ctx;
}
